import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Alert,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    List,
    ListItem,
    ListItemText,
    Snackbar,
    Tabs,
    Tab,
    Avatar,
} from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import {
    DataGrid,
    GridColDef,
    GridValueGetter,
    GridRenderCellParams,
    GridRowSelectionModel,
    GridRowsProp,
    GridRowParams,
} from "@mui/x-data-grid";
import apiClient from "../../utils/apiClient";
import { useAppSelector } from "../../store/hook";
import { selectCurrentUser } from "../../store/slices/authSlice";
import styles from "./AdminPage.module.scss";

// Define structure for raw Post data (adjust based on actual table attributes)
interface RawPostItem {
    postId: string;
    id: string; // for key
    userId: string; // Author's ID
    author: {
        authorId: string;
        name: string;
        profileImageUrl: string;
    };
    content?: {
        text: string;
        media?: any[];
    };
    engagement: {
        likesCount: number;
        commentsCount: number;
        shareCount: number;
    };
    postMetadata: {
        category: string;
        postDate: string;
    };
    imageUrl?: string;
    createdAt?: string; // Assuming ISO string
    updatedAt?: string; // Assuming ISO string
    // Add other relevant post attributes you want to display
    [key: string]: any; // Allow other arbitrary attributes from DDB
}

// Define structure for raw Notification data (adjust based on actual table attributes)
interface RawNotificationItem {
    notificationId: string;
    recipientId: string;
    senderId?: string; // Optional if system generated
    type: string;
    message?: string;
    status: "READ" | "UNREAD";
    createdAt?: string; // Assuming ISO string
    updatedAt?: string; // Assuming ISO string
    relatedEntityId?: string; // Optional, like postId or connectionId
    // Add other relevant attributes you want to display
    [key: string]: any; // Allow other arbitrary attributes from DDB
}

// Define the structure of the combined user data we expect from the API
interface CombinedUser {
    userId: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    createdAt?: string; // Expecting ISO string from backend
    initialSetupComplete?: boolean;
    location?: { country?: string; state?: string; city?: string };
    photoUrl?: string;
    professionalStatus?: string;
    profileLastUpdated?: string; // Expecting ISO string from backend
    isAdmin: boolean;
    joiningReason?: string;
}

const CONFIRMATION_STRING = "confirm delete";

const ReadMoreCell: React.FC<{ text: string | null | undefined; maxLength?: number }> = ({
    text,
    maxLength = 100,
}) => {
    const [expanded, setExpanded] = useState(false);

    if (!text) {
        return null;
    }

    if (text.length <= maxLength) {
        // Use Typography to ensure content wraps and respects theme
        return (
            <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                {text}
            </Typography>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                {expanded ? text : `${text.substring(0, maxLength)}...`}
            </Typography>
            <Button
                size="small"
                onClick={(e) => {
                    e.stopPropagation(); // Prevent row click events
                    setExpanded(!expanded);
                }}
                sx={{ p: 0, m: "4px 0 0 0", textTransform: "none", alignSelf: "flex-start" }}
            >
                {expanded ? "Read Less" : "Read More"}
            </Button>
        </Box>
    );
};

const AdminPage: React.FC = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const navigate = useNavigate();

    // State for user data fetching
    const [users, setUsers] = useState<CombinedUser[]>([]);
    const [isUsersLoading, setIsUsersLoading] = useState<boolean>(false);
    const [usersError, setUsersError] = useState<string | null>(null);

    // State for selected user IDs
    const [selectedUserIds, setSelectedUserIds] = useState<GridRowSelectionModel>([]);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [confirmationText, setConfirmationText] = useState<string>("");
    const [usersToDelete, setUsersToDelete] = useState<CombinedUser[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState<string>("1");

    // State for Posts data fetching
    const [posts, setPosts] = useState<RawPostItem[]>([]);
    const [isPostsLoading, setIsPostsLoading] = useState<boolean>(false);
    const [postsError, setPostsError] = useState<string | null>(null);
    const [postToDelete, setPostToDelete] = useState<RawPostItem | null>(null);
    const [isDeletePostModalOpen, setIsDeletePostModalOpen] = useState<boolean>(false);
    const [isDeletingPost, setIsDeletingPost] = useState<boolean>(false);

    // NEW: State for Notifications data fetching
    const [notificationsData, setNotificationsData] = useState<RawNotificationItem[]>([]);
    const [loadingNotifications, setLoadingNotifications] = useState<boolean>(false);
    const [notificationsError, setNotificationsError] = useState<string | null>(null);

    // Get current user from Redux store
    const currentUser = useAppSelector(selectCurrentUser);

    // Effect for checking admin authorization
    useEffect(() => {
        const checkAdminAccess = async () => {
            setIsLoading(true);
            setAuthError(null);
            try {
                // console log the user group and role
                // console.log("Current User:", currentUser);
                // Log Cognito groups from Redux state
                // console.log("Cognito Groups:", currentUser?.groups || "Not available in state");

                await apiClient.get("/api/admin/check");
                // console.log("🙏 HELLO ADMIN");
                setIsAuthorized(true);
            } catch (err) {
                console.error("Admin access check failed:", err);
                setIsAuthorized(false);
                const error = err as { response?: { status: number } };
                if (error.response?.status === 403) {
                    setAuthError("Access Denied: You do not have permission to view this page.");
                } else {
                    setAuthError("An error occurred while checking permissions.");
                }
            } finally {
                setIsLoading(false);
            }
        };

        checkAdminAccess();
    }, []);

    const fetchUsers = async () => {
        setIsUsersLoading(true);
        setUsersError(null);
        try {
            const response = await apiClient.get<CombinedUser[]>("/api/admin/users");
            setUsers(response.data);
        } catch (err) {
            console.error("Failed to fetch admin users:", err);
            setUsersError("Failed to load user data. Please try again later.");
            setUsers([]);
        } finally {
            setIsUsersLoading(false);
        }
    };

    const fetchPosts = async () => {
        setIsPostsLoading(true);
        setPostsError(null);
        try {
            // console.log("Fetching admin posts...");
            const response = await apiClient.get<RawPostItem[]>("/api/admin/posts");
            setPosts(response.data);
            console.log(`Fetched ${response.data.length} posts.`);
            console.log(response.data);
        } catch (err) {
            console.error("Failed to fetch admin posts:", err);
            setPostsError("Failed to load posts data. Please try again later.");
            setPosts([]);
        } finally {
            setIsPostsLoading(false);
        }
    };

    // NEW: Function to fetch notifications data
    const fetchNotifications = async () => {
        setLoadingNotifications(true);
        setNotificationsError(null);
        try {
            // console.log("Fetching admin notifications...");
            const response = await apiClient.get<RawNotificationItem[]>("/api/admin/notifications");
            setNotificationsData(response.data);
            console.log(`Fetched ${response.data.length} notifications.`);
        } catch (err) {
            console.error("Failed to fetch admin notifications:", err);
            setNotificationsError("Failed to load notifications data. Please try again later.");
            setNotificationsData([]);
        } finally {
            setLoadingNotifications(false);
        }
    };

    // Effect for fetching data based on the active tab
    useEffect(() => {
        if (!isAuthorized) return;

        if (currentTab === "1") {
            fetchUsers();
        } else if (currentTab === "2") {
            fetchPosts();
        } else if (currentTab === "3") {
            // NEW: Fetch notifications for tab 3
            fetchNotifications();
        }
        // Add conditions for other tabs later
    }, [isAuthorized, currentTab]); // Rerun when auth status or tab changes

    const handleGoToNewsfeed = () => {
        navigate("/newsfeed");
    };

    const showSnackbar = (message: string) => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleDeleteSelected = () => {
        if (!currentUser?.id) {
            showSnackbar("Error: Could not identify current user.");
            return;
        }

        const selectedUsersData = users.filter((user) => selectedUserIds.includes(user.userId));

        // Client-side check: Prevent self-delete
        if (selectedUserIds.includes(currentUser.id)) {
            showSnackbar("Error: You cannot delete your own account.");
            return;
        }

        // Client-side check: Prevent deleting other admins
        const adminSelected = selectedUsersData.some((user) => user.isAdmin);
        if (adminSelected) {
            showSnackbar("Error: Cannot delete other administrators.");
            return;
        }

        if (selectedUsersData.length > 0) {
            setUsersToDelete(selectedUsersData);
            setIsModalOpen(true);
        } else {
            console.warn("Delete clicked but no valid users found for deletion.");
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setConfirmationText("");
        setUsersToDelete([]);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        handleCloseModal();
        showSnackbar(`Attempting to delete ${usersToDelete.length} user(s)...`);

        const deletionPromises = usersToDelete.map((user) =>
            apiClient
                .delete(`/api/admin/users/${user.userId}`)
                .then(() => ({ status: "fulfilled", userId: user.userId }))
                .catch((error) => ({ status: "rejected", userId: user.userId, error })),
        );

        const results = await Promise.allSettled(deletionPromises);

        let successCount = 0;
        const errors: string[] = [];

        results.forEach((result) => {
            if (result.status === "fulfilled") {
                const value = result.value;
                if (value.status === "fulfilled") {
                    successCount++;
                } else if (value.status === "rejected") {
                    const rejectedValue = value as {
                        status: "rejected";
                        userId: string;
                        error: any;
                    };
                    const userError = rejectedValue.error;
                    console.error(`Failed to delete user ${rejectedValue.userId}:`, userError);
                    errors.push(
                        `User ${rejectedValue.userId}: ${userError?.response?.data?.message || userError?.message || "Unknown error"}`,
                    );
                }
            } else if (result.status === "rejected") {
                // Handle errors from Promise.allSettled itself
                // ... (existing rejected status handling) ...
            }
        });

        if (errors.length > 0) {
            showSnackbar(
                `Deletion failed for ${errors.length} user(s). ${successCount} succeeded. Errors: ${errors.join("; ")}`,
            );
        } else {
            showSnackbar(`Successfully deleted ${successCount} user(s).`);
        }

        setSelectedUserIds([]);
        setIsDeleting(false);
        fetchUsers();
    };

    const handleOpenDeletePostModal = (post: RawPostItem) => {
        setPostToDelete(post);
        setIsDeletePostModalOpen(true);
    };

    const handleCloseDeletePostModal = () => {
        setPostToDelete(null);
        setIsDeletePostModalOpen(false);
    };

    const handleConfirmDeletePost = async () => {
        if (!postToDelete) return;

        setIsDeletingPost(true);
        try {
            // Use the existing POST endpoint for deleting posts
            console.log("Deleting post:", postToDelete);
            await apiClient.post(`/api/posts/deletePost`, { postId: postToDelete.id });
            showSnackbar(`Successfully deleted post ${postToDelete.id}.`);
            fetchPosts(); // Refresh the posts list
        } catch (err) {
            console.error("Failed to delete post:", err);
            const error = err as { response?: { data?: { message?: string } }; message?: string };
            showSnackbar(`Error deleting post: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsDeletingPost(false);
            handleCloseDeletePostModal();
        }
    };

    // --- Column Definitions for DataGrid ---
    const userColumns: GridColDef[] = [
        // { field: 'userId', headerName: 'User ID', width: 250 }, // Often hidden or used just for ID
        { field: "firstName", headerName: "First Name", width: 150, editable: false },
        { field: "lastName", headerName: "Last Name", width: 150, editable: false },
        { field: "email", headerName: "Email", width: 250, editable: false },
        {
            field: "location",
            headerName: "Location",
            width: 200,
            sortable: false,
            renderCell: (params: GridRenderCellParams<CombinedUser>) => {
                const location = params.row.location;
                if (!location) return "-";
                const { city, state, country } = location;
                return [city, state, country].filter(Boolean).join(", ") || "-";
            },
        },
        {
            field: "isAdmin",
            headerName: "Admin?",
            width: 100,
            type: "boolean",
            renderCell: (params: GridRenderCellParams<CombinedUser>) =>
                params.row.isAdmin ? (
                    <Chip label="Yes" color="success" size="small" />
                ) : (
                    <Chip label="No" size="small" />
                ),
        },
        {
            field: "initialSetupComplete",
            headerName: "Setup Complete?",
            type: "boolean",
            width: 150,
        },
        {
            field: "joiningReason",
            headerName: "Joining Reason",
            width: 300,
            sortable: false,
            renderCell: (params: GridRenderCellParams<CombinedUser>) => (
                <ReadMoreCell text={params.row.joiningReason || ""} />
            ),
        },
        { field: "professionalStatus", headerName: "Prof. Status", width: 150 },
        {
            field: "createdAt",
            headerName: "Created At",
            type: "dateTime",
            width: 180,
            valueGetter: (value: string | undefined | null): Date | null =>
                value ? new Date(value) : null,
        },
        {
            field: "profileLastUpdated",
            headerName: "Profile Updated",
            type: "dateTime",
            width: 180,
            valueGetter: (value: string | undefined | null): Date | null =>
                value ? new Date(value) : null,
        },
        // { field: 'photoUrl', headerName: 'Photo URL', width: 150 }, // Optional
    ];

    // Define columns for the Posts table - adjust fields as needed!
    const postColumns: GridColDef[] = [
        {
            field: "author",
            headerName: "Author",
            width: 200,
            renderCell: (params: GridRenderCellParams<RawPostItem>) => (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                        src={params.row.author?.profileImageUrl}
                        sx={{ width: 32, height: 32, mr: 1 }}
                    />
                    <Typography variant="body2">
                        {params.row.author?.name || params.row.userId}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "content",
            headerName: "Content",
            width: 300,
            valueGetter: (value: RawPostItem["content"]) => {
                if (typeof value === "object" && value?.text) {
                    return value.text;
                }
                return value;
            },
        },
        {
            field: "likes",
            headerName: "Likes",
            width: 100,
            sortable: false,
            renderCell: (params: GridRenderCellParams<RawPostItem>) =>
                params.row.engagement?.likesCount ?? 0,
        },
        {
            field: "comments",
            headerName: "Comments",
            width: 100,
            sortable: false,
            renderCell: (params: GridRenderCellParams<RawPostItem>) =>
                params.row.engagement?.commentsCount ?? 0,
        },
        {
            field: "postMetadata",
            headerName: "Category",
            width: 150,
            valueGetter: (value: any) => value?.category ?? "N/A",
        },
        // {
        // field: "createdAt",
        //     field: "postMetadata",
        //     headerName: "Created At",
        //     type: "dateTime",
        //     width: 180,
        //     valueGetter: (value: setDate | undefined | null): Date | null =>
        //         value ? new Date(value.postDate) : null,
        // },
        // {
        //     field: "updatedAt",
        //     headerName: "Updated At",
        //     type: "dateTime",
        //     width: 180,
        //     valueGetter: (value: string | undefined | null): Date | null =>
        //         value ? new Date(value) : null,
        // },
        // { field: "imageUrl", headerName: "Image URL", width: 200 },
        // Add more columns for other raw attributes if desired
        {
            field: "actions",
            headerName: "Actions",
            type: "actions",
            width: 100,
            getActions: (params) => [
                <Button
                    key={`delete-${params.id}`}
                    color="error"
                    onClick={() => handleOpenDeletePostModal(params.row)}
                    disabled={isDeletingPost}
                >
                    Delete
                </Button>,
            ],
        },
    ];

    // NEW: Define columns for the Notifications DataGrid
    const notificationColumns: GridColDef[] = [
        { field: "notificationId", headerName: "Notification ID", width: 250 },
        { field: "recipientId", headerName: "Recipient ID", width: 250 },
        {
            field: "senderId",
            headerName: "Sender ID",
            width: 250,
            sortable: false,
            valueGetter: (value) => value || "System",
        },
        { field: "type", headerName: "Type", width: 150 },
        { field: "message", headerName: "Message", width: 300, sortable: false },
        { field: "status", headerName: "Status", width: 100 },
        {
            field: "createdAt",
            headerName: "Created At",
            width: 180,
            valueGetter: (value) => (value ? new Date(value) : null),
            type: "dateTime",
        },
        {
            field: "updatedAt",
            headerName: "Updated At",
            width: 180,
            valueGetter: (value) => (value ? new Date(value) : null),
            type: "dateTime",
        },
        { field: "relatedEntityId", headerName: "Related ID", width: 250, sortable: false },
        // Add more columns for other raw notification attributes if needed
    ];

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setCurrentTab(newValue);
    };

    // Function to get the correct ID for DataGrid rows based on the active tab
    const getRowId = (row: GridRowsProp[number]): string | number => {
        if (currentTab === "1" && "userId" in row) {
            return row.userId;
        } else if (currentTab === "2" && "postId" in row) {
            return row.postId;
        } else if (currentTab === "3" && "notificationId" in row) {
            // NEW: Handle notifications
            return row.notificationId;
        }
        // Fallback, although ideally should always match one of the above
        return Math.random();
    };

    // -- Render Logic --

    if (isLoading) {
        // Initial Auth loading
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!isAuthorized) {
        // Auth failed or denied
        return (
            <Box sx={{ padding: 4, textAlign: "center" }}>
                <Alert severity="error" sx={{ marginBottom: 2 }}>
                    {authError || "Access Denied: You do not have permission to view this page."}
                </Alert>
                <Button variant="contained" onClick={handleGoToNewsfeed}>
                    Go to Newsfeed
                </Button>
            </Box>
        );
    }

    // --- Authorized Admin Page Content ---
    return (
        <Box
            sx={{
                padding: 3,
                width: "100%",
                minHeight: "calc(100vh - 64px)", // Allow the container to grow
            }}
        >
            <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
                Warning: You are in the Admin Area. Changes made here can directly affect
                application data and user accounts.
            </Alert>

            <Typography variant="h4" gutterBottom sx={{ flexShrink: 0 }}>
                Admin Dashboard
            </Typography>

            <TabContext value={currentTab}>
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2, flexShrink: 0 }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        aria-label="Admin data tables"
                    >
                        <Tab label="Users & Profiles" value="1" />
                        <Tab label="Posts" value="2" />
                        <Tab label="Notifications" value="3" />
                    </Tabs>
                </Box>

                <TabPanel value="1" sx={{ padding: 0 }} className={styles.tabPanel}>
                    <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={selectedUserIds.length === 0 || isDeleting}
                            onClick={handleDeleteSelected}
                        >
                            {isDeleting
                                ? "Deleting..."
                                : `Delete Selected (${selectedUserIds.length})`}
                        </Button>
                    </Box>

                    {usersError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {usersError}
                        </Alert>
                    )}

                    <Box className={styles.adminTable}>
                        <DataGrid
                            rows={users}
                            columns={userColumns}
                            getRowId={(row) => row.userId}
                            loading={isUsersLoading || isDeleting}
                            checkboxSelection
                            autoHeight
                            onRowSelectionModelChange={(newSelectionModel) => {
                                setSelectedUserIds(newSelectionModel);
                            }}
                            rowSelectionModel={selectedUserIds}
                            disableRowSelectionOnClick
                            pageSizeOptions={[15, 25, 50, 100]}
                            getRowHeight={() => "auto"}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 15 },
                                },
                                columns: {
                                    columnVisibilityModel: {
                                        userId: false,
                                    },
                                },
                            }}
                        />
                    </Box>
                </TabPanel>

                <TabPanel value="2" sx={{ padding: 0 }} className={styles.tabPanel}>
                    {postsError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {postsError}
                        </Alert>
                    )}

                    <Box className={styles.adminTable}>
                        <DataGrid
                            rows={posts}
                            columns={postColumns}
                            getRowId={getRowId}
                            loading={isPostsLoading}
                            autoHeight
                            pageSizeOptions={[15, 25, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 50 },
                                },
                                columns: {
                                    columnVisibilityModel: {
                                        postId: false,
                                        userId: false,
                                    },
                                },
                            }}
                        />
                    </Box>
                </TabPanel>

                <TabPanel value="3" sx={{ padding: 0 }} className={styles.tabPanel}>
                    {notificationsError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {notificationsError}
                        </Alert>
                    )}

                    <Box className={styles.adminTable}>
                        <DataGrid
                            rows={notificationsData}
                            columns={notificationColumns}
                            getRowId={getRowId}
                            loading={loadingNotifications}
                            autoHeight
                            pageSizeOptions={[15, 25, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 50 },
                                },
                            }}
                        />
                    </Box>
                </TabPanel>
            </TabContext>

            <Dialog open={isModalOpen} onClose={handleCloseModal}>
                <DialogTitle sx={{ backgroundColor: "error.main", color: "white" }}>
                    Confirm Permanent Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mt: 2, mb: 1 }}>
                        You are about to permanently delete the following user(s) and all associated
                        data (profiles, posts, connections, messages, etc.). This action cannot be
                        undone.
                    </DialogContentText>
                    <List dense>
                        {usersToDelete.map((user) => (
                            <ListItem key={user.userId}>
                                <ListItemText
                                    primary={`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                                    secondary={user.email}
                                />
                            </ListItem>
                        ))}
                    </List>
                    <DialogContentText sx={{ mt: 2, mb: 1 }} component="div">
                        To confirm, please type <b>&quot;{CONFIRMATION_STRING}&quot;</b> in the box
                        below.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="confirmation"
                        label="Type to confirm"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        error={confirmationText !== "" && confirmationText !== CONFIRMATION_STRING}
                        helperText={
                            confirmationText !== "" && confirmationText !== CONFIRMATION_STRING
                                ? "Text does not match."
                                : ""
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px" }}>
                    <Button onClick={handleCloseModal} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={confirmationText !== CONFIRMATION_STRING || isDeleting}
                    >
                        {isDeleting ? "Deleting..." : "Confirm Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isDeletePostModalOpen} onClose={handleCloseDeletePostModal}>
                <DialogTitle sx={{ backgroundColor: "error.main", color: "white" }}>
                    Confirm Post Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mt: 2, mb: 1 }}>
                        Are you sure you want to permanently delete this post? This action cannot be
                        undone.
                    </DialogContentText>
                    <Typography variant="body2">Post ID: {postToDelete?.id}</Typography>
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px" }}>
                    <Button onClick={handleCloseDeletePostModal} disabled={isDeletingPost}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDeletePost}
                        variant="contained"
                        color="error"
                        disabled={isDeletingPost}
                    >
                        {isDeletingPost ? "Deleting..." : "Confirm Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </Box>
    );
};

export default AdminPage;
